/**
 * src/components/pages/RegisterPage.js
 *
 * Página pública /registrar. Cualquiera puede llenar el formulario para
 * proponer un anuncio Oro o una Promoción.
 *
 * Las imágenes/PDF se suben a Cloudinary (solo viajan las URLs). Al enviar:
 *  1. Se genera un archivo JSON con toda la info → el visitante lo descarga.
 *  2. Se envía una copia a ventas@ubiyaaacomercio.com (EmailJS) como respaldo.
 *
 * El administrador importa ese JSON en su panel; el anuncio entra
 * DESHABILITADO hasta que el admin active la visibilidad.
 */

import '@/styles/register-page.css'
import { define, c } from '@components/Component.js'
import { h } from '@core/dom.js'
import { signal, effect } from '@core/signal.js'
import { directoryData } from '@/db/directory.db.js'
import { publicUploadButton } from '@/shared/publicUpload.js'
import { saveSolicitud } from '@/services/solicitudes.js'
import { cleanWhatsapp, normalizeSocial } from '@/shared/utils/whatsapp.js'

const EMAILJS = {
    service:  'service_m3dtonq',
    template: 'template_4gmcwfa',
    user:     'ns5mXXbWzkaqQy2co',
}

const CATEGORIES = directoryData.categories.map(c => ({ name: c.name, slug: c.slug }))

/* ── Helpers de formulario ───────────────────────────────── */
const field = (label, control, hint) =>
    h('div', { className: 'reg-field' },
        h('label', { className: 'reg-label' }, label),
        control,
        ...(hint ? [h('p', { className: 'reg-hint' }, hint)] : [])
    )

const input = (name, { type = 'text', placeholder = '' } = {}) =>
    h('input', { className: 'reg-input', name, type, placeholder })

const textarea = (name, { placeholder = '', rows = 3 } = {}) =>
    h('textarea', { className: 'reg-textarea', name, rows, placeholder })

const categorySelect = () => {
    const sel = h('select', { className: 'reg-input', name: 'category' })
    const hidden = h('input', { type: 'hidden', name: 'categorySlug' })
    const ph = h('option', { value: '' }, '— Selecciona una categoría —')
    ph.disabled = true; ph.selected = true
    sel.appendChild(ph)
    CATEGORIES.forEach(cat => {
        const o = h('option', { value: cat.name }, cat.name)
        o.dataset.slug = cat.slug
        sel.appendChild(o)
    })
    sel.addEventListener('change', () => {
        const opt = sel.selectedOptions[0]
        hidden.value = opt ? (opt.dataset.slug || '') : ''
    })
    const wrap = h('span', { style: 'display:block' })
    wrap.appendChild(sel); wrap.appendChild(hidden)
    return wrap
}

const get = (form, name) => form.querySelector(`[name="${name}"]`)?.value?.trim() ?? ''

/* ── Formulario de negocio (Oro) ────────────────────────── */
function businessForm(plan) {
    const galleryMax = 10

    const logoInp    = input('logo',    { placeholder: 'Logo del negocio' })
    const imageInp   = input('image',   { placeholder: 'Imagen principal / banner' })
    const galleryInp = textarea('gallery', { placeholder: `Una URL por línea (hasta ${galleryMax} fotos)`, rows: 3 })
    const catalogInp = input('catalogUrl', { placeholder: 'Catálogo o menú (PDF/imagen)' })

    const form = h('form', { className: 'reg-form' },
        h('div', { className: 'reg-grid' },
            field('Nombre del negocio *', input('name', { placeholder: 'Ej: Restaurante El Sabor' })),
            field('Categoría *', categorySelect()),
        ),
        field('Descripción corta *', input('description', { placeholder: 'Una línea para las tarjetas del directorio' })),
        field('Descripción completa', textarea('fullDescription', { placeholder: 'Cuenta todo sobre tu negocio', rows: 5 })),

        h('div', { className: 'reg-section' }, '📸 Imágenes y multimedia'),
        h('div', { className: 'reg-grid' },
            field('Logo', publicUploadButton(logoInp), 'Sube tu logo o pega un enlace'),
            field('Imagen principal', publicUploadButton(imageInp)),
        ),
        field('Galería de fotos', publicUploadButton(galleryInp, { multiple: true, maxLines: galleryMax }),
            'Hasta 10 fotos. Puedes subir varias a la vez.'),
        field('Video (YouTube)', input('video', { placeholder: 'Pega el enlace de YouTube en cualquier formato' })),
        field('Catálogo / Menú (PDF o imagen)', publicUploadButton(catalogInp, { accept: 'image/*,application/pdf' })),

        h('div', { className: 'reg-section' }, '📍 Contacto y ubicación'),
        h('div', { className: 'reg-grid' },
            field('WhatsApp (con código país) *', input('whatsapp', { placeholder: '573124288323' })),
            field('Dirección', input('location', { placeholder: 'Ej: Cra. 7 #10-15, Fusagasugá' })),
        ),
        h('div', { className: 'reg-grid' },
            field('Instagram', input('instagram', { placeholder: 'https://instagram.com/…' })),
            field('Facebook', input('facebook', { placeholder: 'https://facebook.com/…' })),
            field('TikTok', input('tiktok', { placeholder: 'https://tiktok.com/@…' })),
            field('Sitio web', input('website', { placeholder: 'https://minegocio.com' })),
        ),
        field('Etiquetas (separadas por coma)', input('tags', { placeholder: 'Restaurante, Italiano, Café' })),

        h('div', { className: 'reg-section' }, '🙋 Tus datos (no se publican)'),
        h('div', { className: 'reg-grid' },
            field('Administrador *', input('responsable', { placeholder: 'Nombre del administrador' })),
            field('Tu correo *', input('contactEmail', { type: 'email', placeholder: 'correo@ejemplo.com' })),
        ),
    )

    form.addEventListener('submit', e => e.preventDefault())

    form._collect = () => ({
        _type: 'business',
        plan,
        name:            get(form, 'name'),
        category:        get(form, 'category'),
        categorySlug:    get(form, 'categorySlug'),
        description:     get(form, 'description'),
        fullDescription: get(form, 'fullDescription'),
        logo:            get(form, 'logo'),
        image:           get(form, 'image'),
        video:           get(form, 'video'),
        gallery:         (form.querySelector('[name=gallery]')?.value ?? '')
                            .split('\n').map(s => s.trim()).filter(Boolean).slice(0, galleryMax),
        catalog: {
            url:         get(form, 'catalogUrl'),
            label:       'Catálogo de productos y servicios',
            description: 'Consulta la oferta completa del negocio',
        },
        location:  get(form, 'location'),
        mapLabel:  get(form, 'location'),
        whatsapp:  cleanWhatsapp(get(form, 'whatsapp')),
        social: normalizeSocial({
            instagram: get(form, 'instagram') || null,
            facebook:  get(form, 'facebook')  || null,
            tiktok:    get(form, 'tiktok')    || null,
            website:   get(form, 'website')   || null,
        }),
        tags: get(form, 'tags').split(',').map(s => s.trim()).filter(Boolean),
        responsable:  get(form, 'responsable'),
        contactEmail: get(form, 'contactEmail'),
    })

    form._validate = () => {
        const d = form._collect()
        if (!d.name)         return 'El nombre del negocio es obligatorio'
        if (!d.categorySlug) return 'Selecciona una categoría'
        if (!d.description)  return 'La descripción corta es obligatoria'
        if (!d.image)        return 'La imagen principal es obligatoria'
        if (!d.whatsapp)     return 'El WhatsApp es obligatorio'
        if (!d.responsable)  return 'Escribe tu nombre'
        if (!d.contactEmail) return 'Escribe tu correo'
        return null
    }

    return form
}

/* ── Formulario de promoción ─────────────────────────────── */
function promoForm() {
    const imageInp = input('image', { placeholder: 'Imagen de la promoción' })

    const tagSel = h('select', { className: 'reg-input', name: 'tag' })
    ;['Descuento', 'Novedad', '2×1', 'Lanzamiento', 'Gratis'].forEach(t =>
        tagSel.appendChild(h('option', { value: t }, t)))

    const form = h('form', { className: 'reg-form' },
        h('div', { className: 'reg-grid' },
            field('Negocio al que pertenece *', input('business', { placeholder: 'Nombre del negocio' })),
            field('Tipo de promoción', tagSel),
        ),
        field('Título de la promoción *', input('title', { placeholder: 'Ej: 20% en pastas artesanales' })),
        field('Descripción', textarea('description', { placeholder: 'Solo la descripción de la promo. La dirección, teléfono y redes van abajo', rows: 4 })),
        h('div', { className: 'reg-grid' },
            field('Imagen', publicUploadButton(imageInp)),
            field('Vence el (opcional)', input('expires', { type: 'date' })),
        ),
        h('div', { className: 'reg-section' }, '📍 Contacto'),
        h('div', { className: 'reg-grid' },
            field('Dirección *', input('direccion', { placeholder: 'Ej: Calle 25 #50-35' })),
            field('Barrio / Sector', input('barrio', { placeholder: 'Ej: Balmoral' })),
        ),
        h('div', { className: 'reg-grid' },
            field('Teléfono / Celular', input('telefono', { placeholder: 'Ej: 3201234567' })),
            field('WhatsApp *', input('whatsapp', { placeholder: 'Ej: 573001234567' })),
        ),
        h('div', { className: 'reg-section' }, '🔗 Redes sociales'),
        h('div', { className: 'reg-grid' },
            field('Facebook', input('facebook', { placeholder: 'https://facebook.com/...' })),
            field('Instagram', input('instagram', { placeholder: 'https://instagram.com/...' })),
        ),
        h('div', { className: 'reg-grid' },
            field('TikTok', input('tiktok', { placeholder: 'https://tiktok.com/@...' })),
        ),
        h('div', { className: 'reg-section' }, '🙋 Tus datos (no se publican)'),
        h('div', { className: 'reg-grid' },
            field('Administrador *', input('responsable', { placeholder: 'Nombre del administrador' })),
            field('Tu correo *', input('contactEmail', { type: 'email', placeholder: 'correo@ejemplo.com' })),
        ),
    )

    form.addEventListener('submit', e => e.preventDefault())

    form._collect = () => ({
        _type: 'promo',
        business:     get(form, 'business'),
        title:        get(form, 'title'),
        tag:          get(form, 'tag'),
        description:  get(form, 'description'),
        image:        get(form, 'image'),
        expires:      get(form, 'expires') || null,
        direccion:    get(form, 'direccion') || null,
        barrio:       get(form, 'barrio') || null,
        telefono:     get(form, 'telefono') || null,
        whatsapp:     get(form, 'whatsapp') || null,
        facebook:     get(form, 'facebook') || null,
        instagram:    get(form, 'instagram') || null,
        tiktok:       get(form, 'tiktok') || null,
        responsable:  get(form, 'responsable'),
        contactEmail: get(form, 'contactEmail'),
    })

    form._validate = () => {
        const d = form._collect()
        if (!d.business)     return 'Escribe el negocio al que pertenece'
        if (!d.title)        return 'El título es obligatorio'
        if (!d.description)  return 'La descripción es obligatoria'
        if (!d.image)        return 'La imagen es obligatoria'
        if (!d.direccion)    return 'La dirección es obligatoria'
        if (!d.whatsapp)     return 'El WhatsApp es obligatorio'
        if (!d.responsable)  return 'Escribe tu nombre'
        if (!d.contactEmail) return 'Escribe tu correo'
        return null
    }

    return form
}

/* ── Envío ───────────────────────────────────────────────── */
async function emailBackup(data) {
    const tipo = data._type === 'promo' ? 'Promoción' : `Anuncio ${data.plan}`
    const nombre = data.name || data.title || data.business || 'Sin nombre'
    try {
        await fetch('https://api.emailjs.com/api/v1.0/email/send', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                service_id:  EMAILJS.service,
                template_id: EMAILJS.template,
                user_id:     EMAILJS.user,
                template_params: {
                    name:    data.responsable || 'Anónimo',
                    email:   data.contactEmail || '',
                    title:   `Nueva solicitud: ${tipo} — ${nombre}`,
                    message: JSON.stringify(data, null, 2),
                },
            }),
        })
    } catch { /* el respaldo por correo es best-effort */ }
}

/* ── Componente ──────────────────────────────────────────── */
define('RegisterPage', () => {
    const activeTab = signal('oro')

    const forms = {
        oro:   businessForm('oro'),
        promo: promoForm(),
    }

    const tabsBar = h('div', { className: 'reg-tabs' })
    const TABS = [
        { id: 'oro',   label: '⭐ Anuncio Oro' },
        { id: 'promo', label: '🎁 Promoción' },
    ]
    const tabBtns = {}
    TABS.forEach(({ id, label }) => {
        const btn = h('button', { className: 'reg-tab', type: 'button' }, label)
        btn.addEventListener('click', () => activeTab.set(id))
        tabBtns[id] = btn
        tabsBar.appendChild(btn)
    })

    const formWrap = h('div', { className: 'reg-form-wrap' },
        forms.oro, forms.promo
    )

    effect(() => {
        const id = activeTab.get()
        Object.entries(tabBtns).forEach(([k, b]) =>
            b.classList.toggle('reg-tab--active', k === id))
        Object.entries(forms).forEach(([k, f]) =>
            f.style.display = k === id ? 'flex' : 'none')
    })

    const submitBtn = h('button', { className: 'reg-submit', type: 'button' }, '📨 Enviar solicitud')

    const successBox = h('div', { className: 'reg-success', style: 'display:none' },
        h('div', { className: 'reg-success__icon' }, '✅'),
        h('h3', {}, '¡Solicitud enviada!'),
        h('p', {}, 'Tu solicitud fue enviada al equipo de UBIYAAA. ' +
                   'Será revisada y publicada próximamente. ¡Gracias!')
    )

    submitBtn.addEventListener('click', async () => {
        const form = forms[activeTab.get()]
        const error = form._validate()
        if (error) { alert('⚠️ ' + error); return }

        submitBtn.disabled = true
        submitBtn.textContent = '⏳ Enviando…'

        const data = form._collect()

        try {
            await saveSolicitud(data)
        } catch (err) {
            alert('❌ No se pudo enviar la solicitud. Intenta de nuevo.')
            submitBtn.disabled = false
            submitBtn.textContent = '📨 Enviar solicitud'
            return
        }

        emailBackup(data)

        submitBtn.style.display = 'none'
        document.querySelector('.reg-card').scrollIntoView({ behavior: 'smooth' })
        successBox.style.display = 'flex'
    })

    return h('div', { className: 'page page--register' },
        c('Header'),
        h('section', { className: 'reg' },
            h('div', { className: 'reg-card' },
                h('span', { className: 'reg-eyebrow' }, 'Publica con nosotros'),
                h('h1', { className: 'reg-title' }, 'Registra tu anuncio en UBIYAAA'),
                h('p', { className: 'reg-lead' },
                    'Llena el formulario según lo que quieras publicar. El equipo de UBIYAAA ' +
                    'revisará tu solicitud y la publicará. Los campos con * son obligatorios.'),
                tabsBar,
                successBox,
                formWrap,
                submitBtn
            )
        ),
        c('Footer')
    )
})
